"use client";

import { useLocale } from "next-intl";
import * as React from "react";
import WorldIcon from "@/assets/world.svg";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { type Locale, locales } from "@/i18n/config";
import { usePathname, useRouter } from "@/i18n/routing";
import { cn } from "@/lib/utils";

export function LanguageSwitcher() {
  const [open, setOpen] = React.useState(false);
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();

  const handleLocaleChange = (newLocale: Locale) => {
    // Use replace to change locale, then refresh to clear Router Cache
    // This ensures all pages reflect the new locale, not cached content
    router.replace(pathname, { locale: newLocale });
    router.refresh();
    setOpen(false);
  };

  const getLocaleShortcut = (loc: Locale) => loc.toUpperCase();

  return (
    <>
      {/* Desktop version with popover */}
      <div className="hidden md:block">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              role="combobox"
              aria-expanded={open}
              aria-label="Select language"
              className={cn(
                "h-auto flex justify-center items-center cursor-pointer p-0 gap-1 text-base font-bold hover:bg-transparent text-black leading-none dark:text-white",
              )}
            >
              <WorldIcon className="!w-[18px] !h-[18px]" />
              {getLocaleShortcut(locale as Locale)}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="max-w-18 p-0 mt-9 bg-gray-50/95 rounded-none">
            <ul className="rounded-none bg-transparent">
              {locales.map(
                (loc) =>
                  loc !== locale && (
                    <li key={loc}>
                      <button
                        type="button"
                        onClick={() => handleLocaleChange(loc)}
                        aria-label={`Switch to ${loc.toUpperCase()}`}
                        className="w-full cursor-pointer font-semibold text-base text-black rounded-none p-2 hover:bg-gray-100 flex items-center justify-center"
                      >
                        {getLocaleShortcut(loc)}
                      </button>
                    </li>
                  ),
              )}
            </ul>
          </PopoverContent>
        </Popover>
      </div>

      {/* Mobile version with inline languages */}
      <div className="flex md:hidden items-center gap-1 font-bold text-black leading-none dark:text-white">
        <WorldIcon className="!w-[18px] !h-[18px]" />
        {locales.map((loc, index) => (
          <React.Fragment key={loc}>
            <button
              type="button"
              onClick={() => handleLocaleChange(loc)}
              className={cn(
                "cursor-pointer hover:opacity-70 transition-opacity",
                loc === locale ? "opacity-100" : "opacity-60",
              )}
              aria-label={`Switch to ${loc.toUpperCase()}`}
            >
              {getLocaleShortcut(loc)}
            </button>
            {index < locales.length - 1 && (
              <div className="w-px h-4 bg-black/30 dark:bg-white/30" />
            )}
          </React.Fragment>
        ))}
      </div>
    </>
  );
}
